import { type RefObject, useEffect, useRef } from 'react'

/** Every way an overlay can be dismissed. All of them go through one `onClose` (P-UI-004). */
export type CloseReason = 'escape' | 'backdrop' | 'close-button' | 'cancel'

const FOCUSABLE = [
  'a[href]',
  'area[href]',
  'button:not([disabled])',
  'input:not([disabled]):not([type="hidden"])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  'iframe',
  '[contenteditable="true"]',
  '[tabindex]:not([tabindex="-1"])',
].join(',')

export function focusableWithin(node: HTMLElement): HTMLElement[] {
  return Array.from(node.querySelectorAll<HTMLElement>(FOCUSABLE)).filter(
    (element) => !element.hasAttribute('inert') && element.getAttribute('aria-hidden') !== 'true',
  )
}

let lockCount = 0
let savedOverflow = ''

function lockScroll() {
  if (lockCount === 0) {
    savedOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
  }
  lockCount++
}

function unlockScroll() {
  lockCount = Math.max(0, lockCount - 1)
  if (lockCount === 0) document.body.style.overflow = savedOverflow
}

/** Make everything outside the overlay host inert, so focus and AT stay inside. */
function inertSiblings(host: HTMLElement): () => void {
  const changed: Element[] = []
  for (const sibling of Array.from(document.body.children)) {
    if (sibling === host || sibling.hasAttribute('inert') || sibling.contains(host)) continue
    sibling.setAttribute('inert', '')
    changed.push(sibling)
  }
  return () => {
    for (const element of changed) element.removeAttribute('inert')
  }
}

export interface OverlayOptions {
  open: boolean
  /** The dialog panel. */
  panelRef: RefObject<HTMLElement | null>
  /** The portal host appended to body (kept interactive while siblings go inert). */
  hostRef: RefObject<HTMLElement | null>
  onEscape: () => void
  initialFocusRef?: RefObject<HTMLElement | null>
}

/**
 * Modal behaviour shared by Dialog and Drawer: moves focus in, traps Tab, routes Escape to
 * the single close path, locks page scroll, inerts the background and returns focus to the
 * element that opened it.
 */
export function useModalOverlay({
  open,
  panelRef,
  hostRef,
  onEscape,
  initialFocusRef,
}: OverlayOptions) {
  const escapeRef = useRef(onEscape)
  escapeRef.current = onEscape

  useEffect(() => {
    const panel = panelRef.current
    const host = hostRef.current
    if (!open || !panel || !host) return
    const opener = document.activeElement instanceof HTMLElement ? document.activeElement : null

    const target = initialFocusRef?.current ?? focusableWithin(panel)[0] ?? panel
    target.focus({ preventScroll: true })

    function onKeyDown(event: KeyboardEvent) {
      if (!panel) return
      if (event.key === 'Escape') {
        event.stopPropagation()
        event.preventDefault()
        escapeRef.current()
        return
      }
      if (event.key !== 'Tab') return
      const items = focusableWithin(panel)
      const first = items[0]
      const last = items[items.length - 1]
      if (!first || !last) {
        event.preventDefault()
        panel.focus()
        return
      }
      const active = document.activeElement
      if (event.shiftKey && (active === first || active === panel)) {
        event.preventDefault()
        last.focus()
      } else if (!event.shiftKey && active === last) {
        event.preventDefault()
        first.focus()
      }
    }

    panel.addEventListener('keydown', onKeyDown)
    lockScroll()
    const restoreInert = inertSiblings(host)
    return () => {
      panel.removeEventListener('keydown', onKeyDown)
      restoreInert()
      unlockScroll()
      // Return focus to the opener unless something else (e.g. a route change) already took it.
      const active = document.activeElement
      const focusIsFree = !active || active === document.body || panel.contains(active)
      if (focusIsFree && opener?.isConnected) opener.focus({ preventScroll: true })
    }
  }, [open, panelRef, hostRef, initialFocusRef])
}
